import type { ReactNode } from 'react'
import type { PermissionKind } from '../../../services/permissions/permissions-service'

/** Per-permission copy + icon shown in the guided onboarding cards/checklist. */
export type PermissionContent = {
  /** Short checklist label. */
  label: string
  /** Pre-prompt card heading. */
  title: string
  /** Friendly "why we need this" body shown before the OS dialog. */
  why: string
  /** Primary button label that triggers the native dialog. */
  allowLabel: string
  /** Browser-specific re-enable hint (web has no settings deep-link). */
  webDeniedHint: string
  /**
   * Numbered Chrome / Safari site-settings steps when the browser has hard-blocked
   * this permission (browsers never re-prompt after Block).
   */
  webDeniedSteps: { chrome: string[]; safari: string[] }
  /**
   * Short, numbered steps shown on the denied card telling the user exactly
   * what to tap once they land in the OS app-settings screen ("Open Settings"
   * deep-links there, but the OS gives no further guidance).
   */
  settingsSteps: { android: string[]; ios: string[] }
  icon: ReactNode
}

function siteHostname(): string {
  if (typeof window !== 'undefined' && window.location.hostname) {
    return window.location.hostname
  }
  return 'this site'
}

/** Shared Chrome / Safari unblock steps for a site permission name. */
function webSiteSettingsSteps(permissionName: string): { chrome: string[]; safari: string[] } {
  const site = siteHostname()
  return {
    chrome: [
      'Click the lock or tune icon in the address bar',
      'Open Site settings',
      `Set ${permissionName} to Allow`,
      'Return here — status updates when you come back',
    ],
    safari: [
      `Safari menu → Settings for ${site}…`,
      `Set ${permissionName} to Allow`,
      'Return here — status updates when you come back',
    ],
  }
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  )
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 11a7 7 0 0014 0M12 18v3" />
    </svg>
  )
}

function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path d="M3 8a2 2 0 012-2h2l1.5-2h7L17 6h2a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <circle cx="12" cy="12.5" r="3.5" />
    </svg>
  )
}

function LocationIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path d="M12 21s-7-5.5-7-11a7 7 0 1114 0c0 5.5-7 11-7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  )
}

export const PERMISSION_CONTENT: Record<PermissionKind, PermissionContent> = {
  notifications: {
    label: 'Notifications',
    title: 'Stay in the loop',
    why: "Get alerted about new messages and incoming calls — even when the app is closed. Without this you'll only see updates while the app is open.",
    allowLabel: 'Allow notifications',
    webDeniedHint:
      'Notifications are blocked for this site. Your browser won’t ask again — use site settings below to Allow them.',
    webDeniedSteps: webSiteSettingsSteps('Notifications'),
    settingsSteps: {
      android: ['Tap Permissions', 'Tap Notifications', 'Turn on Allow notifications'],
      ios: ['Tap Notifications', 'Turn on Allow Notifications'],
    },
    icon: <BellIcon />,
  },
  microphone: {
    label: 'Microphone',
    title: 'Be heard on calls',
    why: 'Ascend needs your microphone for voice and video calls. Without it, others won’t be able to hear you.',
    allowLabel: 'Allow microphone',
    webDeniedHint:
      'Microphone is blocked for this site. Your browser won’t ask again — use site settings below to Allow it.',
    webDeniedSteps: webSiteSettingsSteps('Microphone'),
    settingsSteps: {
      android: ['Tap Permissions', 'Tap Microphone', 'Choose "Allow only while using the app"'],
      ios: ['Tap Microphone', 'Turn the switch on'],
    },
    icon: <MicIcon />,
  },
  camera: {
    label: 'Camera',
    title: 'Turn on video',
    why: 'Your camera powers video calls and lets you snap a photo to share in chat. You can always join with video off.',
    allowLabel: 'Allow camera',
    webDeniedHint:
      'Camera is blocked for this site. Your browser won’t ask again — use site settings below to Allow it.',
    webDeniedSteps: webSiteSettingsSteps('Camera'),
    settingsSteps: {
      android: ['Tap Permissions', 'Tap Camera', 'Choose "Allow only while using the app"'],
      ios: ['Tap Camera', 'Turn the switch on'],
    },
    icon: <CameraIcon />,
  },
  location: {
    label: 'Location',
    title: 'Share where you are',
    why: 'During a call you can share your live location with a colleague for safety and coordination — handy in an urgent situation. We only access it when you choose to share.',
    allowLabel: 'Allow location',
    webDeniedHint:
      'Location is blocked for this site. Your browser won’t ask again — use site settings below to Allow it.',
    webDeniedSteps: webSiteSettingsSteps('Location'),
    settingsSteps: {
      android: ['Tap Permissions', 'Tap Location', 'Choose "Allow only while using the app"'],
      ios: ['Tap Location', 'Choose "While Using the App"'],
    },
    icon: <LocationIcon />,
  },
}
